import { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Ban, RefreshCw, Search, Eye, MapPin, Trash2, ShieldAlert, ShieldCheck, Globe, Cookie, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import API from '@/lib/api';

const EVENT_LABELS = {
  login_success: { label: 'Login OK', tone: 'emerald' },
  login_failed: { label: 'Login fallido', tone: 'amber' },
  login_blocked: { label: 'Login bloqueado', tone: 'red' },
  logout: { label: 'Logout', tone: 'neutral' },
  token_revoked: { label: 'Token revocado', tone: 'amber' },
  ip_blocked_manual: { label: 'IP bloqueada (manual)', tone: 'red' },
  ip_unblocked_manual: { label: 'IP desbloqueada', tone: 'blue' },
  password_changed: { label: 'Cambio password', tone: 'blue' },
  password_reset_requested: { label: 'Reset solicitado', tone: 'neutral' },
  password_reset_completed: { label: 'Reset completado', tone: 'emerald' },
  role_changed: { label: 'Cambio de rol', tone: 'amber' },
  suspicious_activity: { label: 'Actividad sospechosa', tone: 'red' },
};

const TONE_CLASSES = {
  emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  amber: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  red: 'bg-red-500/10 text-red-300 border-red-500/30',
  blue: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  neutral: 'bg-muted/40 text-muted-foreground border-border',
};

function StatCard({ icon: Icon, label, value, tone = 'neutral', testId }) {
  const toneCls = {
    neutral: 'text-muted-foreground bg-muted/40',
    emerald: 'text-emerald-400 bg-emerald-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    red: 'text-red-400 bg-red-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
  };
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${toneCls[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-3xl font-bold tabular-nums">{value}</span>
      </div>
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function SecurityDashboard() {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filtros tabla eventos
  const [filterType, setFilterType] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterIP, setFilterIP] = useState('');
  const [filterHours, setFilterHours] = useState(24);

  // Logs de consentimiento RGPD
  const [consents, setConsents] = useState([]);
  const [consentStats, setConsentStats] = useState(null);
  const [consentHours, setConsentHours] = useState(168); // 7 días
  const [exportingCsv, setExportingCsv] = useState(false);

  // Diálogo bloquear IP
  const [showBlock, setShowBlock] = useState(false);
  const [blockIPValue, setBlockIPValue] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blockDuration, setBlockDuration] = useState('1440'); // 24h por defecto
  const [blocking, setBlocking] = useState(false);

  useEffect(() => {
    loadData();
    const t = setInterval(() => loadData(true), 60000); // auto-refresh 1 min
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterEmail, filterIP, filterHours]);

  useEffect(() => {
    loadConsents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentHours]);

  const loadData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      const { data } = await API.get('/security/dashboard');
      setData(data);
    } catch (e) {
      toast.error('Error cargando panel de seguridad');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadEvents = async () => {
    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (filterType) params.set('type', filterType);
      if (filterEmail) params.set('email', filterEmail);
      if (filterIP) params.set('ip', filterIP);
      if (filterHours) params.set('hours', String(filterHours));
      const { data } = await API.get(`/security/events?${params}`);
      setEvents(data.events || []);
    } catch (e) {
      // silenciado
    }
  };

  const loadConsents = async () => {
    try {
      const params = new URLSearchParams();
      params.set('limit', '200');
      if (consentHours) params.set('hours', String(consentHours));
      const { data } = await API.get(`/security/consent-logs?${params}`);
      setConsents(data.items || []);
      setConsentStats(data.stats || null);
    } catch (e) {
      // silenciado
    }
  };

  const handleExportConsentCsv = async () => {
    setExportingCsv(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (consentHours) params.set('hours', String(consentHours));
      params.set('token', token);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/security/consent-logs/export.csv?${params}`;
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Error exportando CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  const handleBlockIP = async () => {
    if (!blockIPValue.trim()) {
      toast.error('Indica una IP');
      return;
    }
    setBlocking(true);
    try {
      await API.post('/security/block-ip', {
        ip: blockIPValue.trim(),
        reason: blockReason || 'Bloqueo manual desde panel',
        duration_minutes: parseInt(blockDuration) || null,
      });
      toast.success(`IP ${blockIPValue} bloqueada`);
      setShowBlock(false);
      setBlockIPValue('');
      setBlockReason('');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error bloqueando IP');
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async (ip) => {
    if (!window.confirm(`¿Desbloquear IP ${ip}?`)) return;
    try {
      await API.post('/security/unblock-ip', { ip });
      toast.success(`IP ${ip} desbloqueada`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleQuickBlock = (ip) => {
    setBlockIPValue(ip);
    setShowBlock(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-muted-foreground">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Cargando…
      </div>
    );
  }

  const stats = data?.stats_24h || {};
  const suspicious = data?.top_suspicious_ips_24h || [];
  const blocked = data?.blocked_ips || [];

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="security-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            Panel de Seguridad
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Eventos · Ataques · Bloqueos · Auditoría · Últimas 24h
          </p>
        </div>
        <Button onClick={() => loadData()} variant="outline" size="sm" disabled={refreshing} data-testid="btn-refresh-security">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Alerta de ataque */}
      {data?.attack_alert && (
        <Card className="border-red-500/40 bg-red-500/5">
          <CardContent className="py-4 flex items-center gap-3">
            <ShieldAlert className="w-5 h-5 text-red-400" />
            <div>
              <p className="font-semibold text-red-300">Posible ataque en curso</p>
              <p className="text-xs text-muted-foreground">
                Detectado un volumen elevado de fallos de login en la última hora desde una o más IPs. Revisa la tabla "IPs sospechosas" debajo.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPIs 24h */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={ShieldCheck} label="Logins OK 24h" value={stats.login_success || 0} tone="emerald" testId="stat-login-success" />
        <StatCard icon={AlertTriangle} label="Fallidos 24h" value={stats.login_failed || 0} tone="amber" testId="stat-login-failed" />
        <StatCard icon={Ban} label="Bloqueados 24h" value={stats.login_blocked || 0} tone="red" testId="stat-login-blocked" />
        <StatCard icon={Globe} label="IPs Bloqueadas" value={blocked.length} tone="red" testId="stat-blocked-ips" />
        <StatCard icon={ShieldAlert} label="Sospechosas" value={suspicious.length} tone="amber" testId="stat-suspicious-ips" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top IPs sospechosas */}
        <Card data-testid="card-suspicious">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-400" />
              Top IPs sospechosas (24h)
            </CardTitle>
            <CardDescription>3+ fallos de login</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {suspicious.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-3 text-center">
                Sin actividad sospechosa
              </p>
            ) : (
              suspicious.map((s) => (
                <div key={s.ip} className="flex items-center justify-between p-2.5 rounded-md border border-border bg-muted/30">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-semibold">{s.ip}</span>
                      {s.country && (
                        <Badge variant="outline" className="text-[10px] gap-1">
                          <MapPin className="w-2.5 h-2.5" /> {s.country_name || s.country}
                          {s.city && ` · ${s.city}`}
                        </Badge>
                      )}
                      <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-[10px]">
                        {s.count} fallos
                      </Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 truncate">
                      Emails: {(s.emails_attempted || []).slice(0, 3).join(', ') || '—'}
                      {(s.emails_attempted || []).length > 3 && ` +${s.emails_attempted.length - 3}`}
                    </p>
                  </div>
                  {(
                    <Button size="sm" variant="outline" onClick={() => handleQuickBlock(s.ip)} className="ml-2 h-7 text-xs">
                      <Ban className="w-3 h-3 mr-1" /> Bloquear
                    </Button>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* IPs bloqueadas */}
        <Card data-testid="card-blocked">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Ban className="w-4 h-4 text-red-400" />
                  IPs Bloqueadas
                </CardTitle>
                <CardDescription>Manual o automático</CardDescription>
              </div>
              {(
                <Button size="sm" variant="outline" onClick={() => setShowBlock(true)} data-testid="btn-block-ip">
                  <Ban className="w-3 h-3 mr-1" /> Bloquear IP
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {blocked.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-3 text-center">
                No hay IPs bloqueadas
              </p>
            ) : (
              blocked.map((b) => (
                <div key={b.ip} className="flex items-center justify-between p-2.5 rounded-md border border-red-500/30 bg-red-500/5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-semibold">{b.ip}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {b.expires_at ? `Hasta ${fmtDate(b.expires_at)}` : 'Permanente'}
                      </Badge>
                      {b.auto && <Badge className="bg-blue-500/10 text-blue-300 text-[10px]">auto</Badge>}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 truncate">
                      {b.reason || '—'} · por {b.blocked_by || 'sistema'}
                    </p>
                  </div>
                  {(
                    <Button size="sm" variant="ghost" onClick={() => handleUnblock(b.ip)} className="ml-2 h-7 text-xs">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabla de eventos */}
      <Card data-testid="card-events">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="w-4 h-4 text-primary" /> Eventos de Seguridad
          </CardTitle>
          <CardDescription>Historial filtrable</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div>
              <Label className="text-xs">Tipo</Label>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                      data-testid="filter-event-type"
                      className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">Todos</option>
                {Object.entries(EVENT_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={filterEmail} onChange={(e) => setFilterEmail(e.target.value)}
                     placeholder="email@ejemplo.com" className="mt-1 h-9" data-testid="filter-event-email" />
            </div>
            <div>
              <Label className="text-xs">IP</Label>
              <Input value={filterIP} onChange={(e) => setFilterIP(e.target.value)}
                     placeholder="1.2.3.4" className="mt-1 h-9 font-mono" data-testid="filter-event-ip" />
            </div>
            <div>
              <Label className="text-xs">Ventana</Label>
              <select value={filterHours} onChange={(e) => setFilterHours(parseInt(e.target.value))}
                      data-testid="filter-event-hours"
                      className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="1">Última 1h</option>
                <option value="24">24h</option>
                <option value="168">7 días</option>
                <option value="720">30 días</option>
              </select>
            </div>
          </div>

          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2">País</th>
                  <th className="px-3 py-2">Detalle</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-3 py-6 text-center text-muted-foreground italic">
                      Sin eventos en la ventana seleccionada
                    </td>
                  </tr>
                ) : (
                  events.map((e) => {
                    const meta = EVENT_LABELS[e.type] || { label: e.type, tone: 'neutral' };
                    return (
                      <tr key={e.id} className="border-t border-border hover:bg-muted/20" data-testid={`event-row-${e.id}`}>
                        <td className="px-3 py-2 text-muted-foreground tabular-nums whitespace-nowrap">{fmtDate(e.timestamp)}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded border text-[10px] ${TONE_CLASSES[meta.tone]}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 truncate max-w-[180px]">{e.email || '—'}</td>
                        <td className="px-3 py-2 font-mono">{e.ip}</td>
                        <td className="px-3 py-2 text-[10px]">
                          {e.country ? `${e.country_name || e.country}${e.city ? ` · ${e.city}` : ''}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground truncate max-w-[260px]">{e.detail || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          {e.ip && e.ip !== 'unknown' && (
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]"
                                    onClick={() => handleQuickBlock(e.ip)}>
                              <Ban className="w-3 h-3" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Logs de Consentimiento RGPD/AEPD */}
      <Card data-testid="card-consent-logs">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Cookie className="w-4 h-4 text-amber-400" />
                Logs de Consentimiento de Cookies
              </CardTitle>
              <CardDescription>
                Auditoría AEPD · cada decisión queda registrada con IP, fecha y preferencias
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <select value={consentHours} onChange={(e) => setConsentHours(parseInt(e.target.value))}
                      data-testid="filter-consent-hours"
                      className="flex h-9 rounded-md border border-input bg-background px-3 text-sm">
                <option value="24">24h</option>
                <option value="168">7 días</option>
                <option value="720">30 días</option>
                <option value="8760">1 año</option>
              </select>
              <Button size="sm" variant="outline" onClick={handleExportConsentCsv}
                      disabled={exportingCsv}
                      data-testid="btn-export-consent-csv">
                <Download className="w-3 h-3 mr-1.5" />
                Exportar CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {consentStats && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-md border border-border bg-muted/30 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total registros</p>
                <p className="text-2xl font-bold tabular-nums">{consents.length}</p>
              </div>
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
                <p className="text-[10px] uppercase tracking-wide text-emerald-300">Aceptaron analíticas</p>
                <p className="text-2xl font-bold tabular-nums text-emerald-300">{consentStats.accepted_analytics || 0}</p>
              </div>
              <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
                <p className="text-[10px] uppercase tracking-wide text-blue-300">Aceptaron marketing</p>
                <p className="text-2xl font-bold tabular-nums text-blue-300">{consentStats.accepted_marketing || 0}</p>
              </div>
            </div>
          )}
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2 text-center">Analítica</th>
                  <th className="px-3 py-2 text-center">Marketing</th>
                  <th className="px-3 py-2">User-Agent</th>
                </tr>
              </thead>
              <tbody>
                {consents.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-3 py-6 text-center text-muted-foreground italic">
                      Sin registros de consentimiento en la ventana seleccionada
                    </td>
                  </tr>
                ) : (
                  consents.slice(0, 50).map((c, idx) => {
                    const prefs = c.preferences || {};
                    return (
                      <tr key={c.id || idx} className="border-t border-border hover:bg-muted/20">
                        <td className="px-3 py-2 text-muted-foreground tabular-nums whitespace-nowrap">{fmtDate(c.server_ts)}</td>
                        <td className="px-3 py-2">
                          <span className="inline-block px-2 py-0.5 rounded border text-[10px] bg-amber-500/10 text-amber-300 border-amber-500/30">
                            {c.type || 'cookies'}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono">{c.ip || '—'}</td>
                        <td className="px-3 py-2 text-center">
                          {prefs.analytics ? (
                            <span className="text-emerald-300">✓</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {prefs.marketing ? (
                            <span className="text-emerald-300">✓</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground truncate max-w-[260px]">{c.user_agent || '—'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {consents.length > 50 && (
            <p className="text-[10px] text-muted-foreground italic mt-2 text-center">
              Mostrando 50 de {consents.length}. Usa "Exportar CSV" para descargar todos.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Modal Bloquear IP */}
      {showBlock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
             onClick={() => setShowBlock(false)}>
          <Card className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Ban className="w-4 h-4 text-red-400" />
                Bloquear IP
              </CardTitle>
              <CardDescription>Acceso denegado a /api/* desde esa IP.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">IP</Label>
                <Input value={blockIPValue} onChange={(e) => setBlockIPValue(e.target.value)}
                       placeholder="1.2.3.4" className="font-mono" data-testid="input-block-ip" />
              </div>
              <div>
                <Label className="text-xs">Motivo</Label>
                <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)}
                       placeholder="Brute force, scanner..." data-testid="input-block-reason" />
              </div>
              <div>
                <Label className="text-xs">Duración (minutos)</Label>
                <select value={blockDuration} onChange={(e) => setBlockDuration(e.target.value)}
                        data-testid="input-block-duration"
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm mt-1">
                  <option value="60">1 hora</option>
                  <option value="1440">24 horas</option>
                  <option value="10080">7 días</option>
                  <option value="43200">30 días</option>
                  <option value="0">Permanente</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowBlock(false)}>Cancelar</Button>
                <Button onClick={handleBlockIP} disabled={blocking} data-testid="btn-confirmar-block">
                  {blocking ? 'Bloqueando…' : 'Bloquear'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
